import { FC, useEffect, useState } from 'react'
import { Container, Paper, Typography, Divider, Box, Stack, Chip } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import GavelIcon from '@mui/icons-material/Gavel'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { solicitacoesTitularService } from '@/services/solicitacoesTitularService'

const DPO_EMAIL = (import.meta.env.VITE_DPO_EMAIL as string | undefined) ?? 'dpo@originei.com.br'

/**
 * Prazo de resposta ao titular — vem do BACKEND (`GET /v1/lgpd/configuracao`,
 * `prazoRespostaDias`; AN-12 A3/P-4, ONDA5 decisão 5). Até 25/10 a página afirmava
 * "15 dias úteis (prazo legal)" em texto fixo, sem validação do Jurídico/DPO. Ausente (não
 * configurado, ou visitante sem sessão — o endpoint exige login) = a página não fala de prazo.
 */
export const usePrazoRespostaDias = (): number | null => {
  const [dias, setDias] = useState<number | null>(null)
  useEffect(() => {
    let vivo = true
    solicitacoesTitularService
      .configuracao()
      .then((c) => {
        if (vivo && typeof c?.prazoRespostaDias === 'number' && c.prazoRespostaDias > 0) setDias(c.prazoRespostaDias)
      })
      .catch(() => undefined)
    return () => {
      vivo = false
    }
  }, [])
  return dias
}

const DpoPage: FC = () => {
  const dias = usePrazoRespostaDias()
  const prazo = dias ? `${dias} dias` : ''
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper sx={{ p: { xs: 3, md: 5 } }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <VerifiedUserIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Encarregado pelo Tratamento de Dados (DPO)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lei Geral de Proteção de Dados — Lei 13.709/2018
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" paragraph>
          O DPO é o canal oficial da Originei para assuntos relacionados ao tratamento de dados
          pessoais. Em conformidade com o Art. 41 da LGPD, mantemos um Encarregado designado para
          atender solicitações de titulares e da ANPD.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ my: 3 }}>
          <Chip icon={<GavelIcon />} label="Lei 13.709/2018" size="small" />
          <Chip label="Art. 41" size="small" variant="outlined" />
        </Stack>

        <Box sx={{ p: 3, bgcolor: 'rgba(255, 98, 12,0.05)', borderRadius: 2, mb: 3 }}>
          <Typography variant="overline" color="primary.main" fontWeight={700}>
            Contato direto
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
              {DPO_EMAIL}
            </Typography>
          </Stack>
          {prazo && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1 }}
              data-testid="dpo-prazo"
            >
              Prazo de resposta informado pela Originei: {prazo}.
            </Typography>
          )}
        </Box>

        <Typography variant="h6" fontWeight={700} sx={{ mt: 3 }} gutterBottom>
          Quando entrar em contato com o DPO
        </Typography>
        <Typography variant="body1" paragraph component="div">
          <ul>
            <li>Solicitar acesso aos seus dados pessoais armazenados na plataforma</li>
            <li>Pedir correção de dados incompletos, inexatos ou desatualizados</li>
            <li>Solicitar anonimização, bloqueio ou eliminação de dados desnecessários</li>
            <li>Pedir portabilidade dos seus dados</li>
            <li>Revogar consentimento previamente dado</li>
            <li>Reportar suspeita de incidente envolvendo dados pessoais</li>
            <li>Tirar dúvidas sobre práticas de tratamento na plataforma</li>
          </ul>
        </Typography>

        <Typography variant="h6" fontWeight={700} sx={{ mt: 3 }} gutterBottom>
          Como sua solicitação é tratada
        </Typography>
        <Typography variant="body1" paragraph>
          1. Recebemos a solicitação por e-mail com confirmação automática.<br />
          2. Validamos sua identidade como titular (ou representante autorizado).<br />
          3. Avaliamos a viabilidade jurídica da solicitação (algumas guardas legais impedem
          eliminação imediata).<br />
          4. Executamos a ação e respondemos formalmente{prazo ? ` em até ${prazo}` : ''}.<br />
          5. Toda interação é registrada para fins de auditoria e prestação de contas (Art. 6º, X).
        </Typography>

        <Divider sx={{ my: 4 }} />
        <Typography variant="caption" color="text.secondary">
          Caso a resposta não seja satisfatória, o titular pode procurar a Autoridade Nacional de
          Proteção de Dados (ANPD) através de{' '}
          <a href="https://www.gov.br/anpd" target="_blank" rel="noreferrer" style={{ color: '#ff620c' }}>
            gov.br/anpd
          </a>
          .
        </Typography>
      </Paper>
    </Container>
  )
}

export default DpoPage
